import axios, { AxiosInstance } from "axios";
import { sha256 } from "js-sha256";
import * as qs from "qs";

const BASE_URL = "https://api.counterapi.dev/v1/";

export const apiConfig = {
  returnRejectedPromiseOnError: true,
  withCredentials: false,
  timeout: 30000,
  baseURL: BASE_URL,
  headers: {
    common: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  },
  paramsSerializer: function (params) {
    return qs.stringify(params, { indices: false });
  },
  validateStatus: function (status) {
    return status === 200;
  },
};

class Counter {
  public ID: number;
  public Name: string;
  public Count: number;
  public CreatedAt: Date;
  public UpdatedAt: Date;

  public constructor(init?: Partial<Counter>) {
    Object.assign(this, init);
  }
}
class Count {
  public Count: number;
  public Date: Date;

  public constructor(init?: Partial<Count>) {
    Object.assign(this, init);
  }
}

class Hash {
  static create(str: string): string {
    const hash = sha256.update(str);
    return hash.hex();
  }
}

export enum GroupByTypes {
  Day = "day",
  Week = "week",
  Month = "month",
  Year = "year",
}

export enum OrderByTypes {
  ASC = "asc",
  DESC = "desc",
}

export interface CountsQuery {
  group_by: GroupByTypes;
  order?: OrderByTypes;
  hash?: boolean;
}

export class CounterAPI {
  axios: AxiosInstance;

  constructor() {
    this.axios = axios.create(apiConfig);
  }

  async up(namespace: string, name: string, hash: boolean = false): Promise<Counter> {
    if (hash) {
      name = Hash.create(name);
    }

    return await this.axios
      .get(`${namespace}/${name}/up`)
      .then((res) => {
        return new Counter({
          ID: res.data.id,
          Name: res.data.name,
          Count: res.data.count,
          UpdatedAt: res.data.updated_at,
          CreatedAt: res.data.created_at,
        });
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async down(namespace: string, name: string, hash: boolean = false): Promise<Counter> {
    if (hash) {
      name = Hash.create(name);
    }

    return await this.axios
      .get(`${namespace}/${name}/down`)
      .then((res) => {
        return new Counter({
          ID: res.data.id,
          Name: res.data.name,
          Count: res.data.count,
          UpdatedAt: res.data.updated_at,
          CreatedAt: res.data.created_at,
        });
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async get(namespace: string, name: string, hash: boolean = false): Promise<Counter> {
    if (hash) {
      name = Hash.create(name);
    }

    return await this.axios
      .get(`${namespace}/${name}/`)
      .then((res) => {
        return new Counter({
          ID: res.data.id,
          Name: res.data.name,
          Count: res.data.count,
          UpdatedAt: res.data.updated_at,
          CreatedAt: res.data.created_at,
        });
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async set(
    namespace: string,
    name: string,
    count: number,
    hash: boolean = false
  ): Promise<Counter> {
    if (hash) {
      name = Hash.create(name);
    }

    return await this.axios
      .get(`${namespace}/${name}/set`, {
        params: {
          count,
        },
      })
      .then((res) => {
        return new Counter({
          ID: res.data.id,
          Name: res.data.name,
          Count: res.data.count,
          UpdatedAt: res.data.updated_at,
          CreatedAt: res.data.created_at,
        });
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  async counts(
      namespace: string,
      name: string,
      query: CountsQuery
  ): Promise<Count[]> {
    if (query.hash) {
      name = Hash.create(name);
    }

    return await this.axios
      .get(`${namespace}/${name}/list`, {
        params: query,
      })
      .then((res) => {
        let counts: Count[] = [];
        res.data.forEach((row) => {
          counts.push(
            new Count({
              Count: row.count,
              Date: row.date,
            })
          );
        });
        return counts;
      })
      .catch((err) => {
        throw new Error(err);
      });
  }
}
